import React from 'react';
import AirportSearchInput from './AirportSearchInput';
import DatePickerInput from './DatePickerInput';
import AIRPORTS from '../data/airports';

// Default international airports to show when clicking airport search fields
const INTERNATIONAL_AIRPORT_SUGGESTIONS = [
  AIRPORTS.find(a => a.code === 'MEL'), // Melbourne
  AIRPORTS.find(a => a.code === 'SYD'), // Sydney
  AIRPORTS.find(a => a.code === 'DEL'), // New Delhi
  AIRPORTS.find(a => a.code === 'BOM'), // Mumbai
  AIRPORTS.find(a => a.code === 'DPS'), // Bali (Denpasar)
  AIRPORTS.find(a => a.code === 'BKK'), // Bangkok
  AIRPORTS.find(a => a.code === 'SIN'), // Singapore
  AIRPORTS.find(a => a.code === 'DXB'), // Dubai
].filter(Boolean);

/**
 * SearchFormTailwind - Premium modern form design 
 * Features: refined spacing, polished inputs, elegant typography
 */
const SearchFormTailwind = ({
  tripType,
  onTripTypeChange,
  formData,
  onInputChange,
  onSwap,
  directOnlySearch,
  onDirectOnlyChange,
  onSubmit,
  loading,
  todayDate,
  minimumReturnDate,
  fieldErrors,
  travellerSummary,
  travellerPanelOpen,
  setTravellerPanelOpen,
  travellerPanelRef,
  handlePassengerCountChange,
  handleClassChange,
  classOptions,
  rescheduleContext,
  returnDatePickerRef,
  setFormData,
  selectedCurrency,
  setSelectedCurrency,
}) => {
  return (
    <form onSubmit={onSubmit} className="space-y-2">

      {/* Main Form Fields */}
      <div className="space-y-2">
        {rescheduleContext?.lockRoute ? (
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">Route</label>
            <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
              <div className="text-sm text-gray-700 font-medium">{formData.from}</div>
              <span className="text-gray-400">→</span>
              <div className="text-sm text-gray-700 font-medium">{formData.to}</div>
            </div>
          </div>
        ) : (
          <div className="relative">
            {/* From Field */}
            <div className="relative">
              <label htmlFor="search-from" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">
                Leaving From
              </label>
              <AirportSearchInput
                id="search-from"
                label={null}
                value={formData.from}
                placeholder="City or Airport"
                onChange={(city) => onInputChange({ target: { name: 'from', value: city } })}
                excludeAirport={formData.to}
                defaultSuggestions={INTERNATIONAL_AIRPORT_SUGGESTIONS}
              />
              
              {/* Swap Button - Positioned to overlap equally between both fields */}
              <button
                type="button"
                onClick={onSwap}
                className="absolute right-0 bottom-[-24px] sm:bottom-[-38px] w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-white border-2 border-gray-300 hover:border-primary hover:bg-blue-50 flex items-center justify-center transition-all shadow-sm hover:shadow-md active:scale-95 group z-20"
                aria-label="Swap departure and destination"
                title="Swap airports"
              >
                <svg 
                  className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500 group-hover:text-primary transition-all group-hover:rotate-180 duration-300" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  strokeWidth="2.5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
              </button>
            </div>

            {/* To Field */}
            <div className="mt-2">
              <label htmlFor="search-to" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">
                Going To
              </label>
              <AirportSearchInput
                id="search-to"
                label={null}
                value={formData.to}
                placeholder="City or Airport"
                onChange={(city) => onInputChange({ target: { name: 'to', value: city } })}
                excludeAirport={formData.from}
                defaultSuggestions={INTERNATIONAL_AIRPORT_SUGGESTIONS}
              />
            </div>
          </div>
        )}

        {/* Dates */}
        <div>
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">
            Dates
          </label>
          <div className={`grid gap-2 ${tripType === 'round-trip' ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <DatePickerInput
              id="date"
              name="date"
              label="Select Departure Date"
              placeholder="Departure"
              minDate={todayDate}
              value={formData.date}
              onChange={onInputChange}
              hasError={!!fieldErrors.date}
            />
            {tripType === 'round-trip' && (
              <DatePickerInput
                ref={returnDatePickerRef}
                id="returnDate"
                name="returnDate"
                label="Select Return Date"
                placeholder="Return"
                minDate={minimumReturnDate}
                value={formData.returnDate}
                onChange={onInputChange}
                disabled={tripType !== 'round-trip'}
                required={tripType === 'round-trip'}
                hasError={!!fieldErrors.returnDate}
              />
            )}
          </div>
        </div>

        {/* Travellers */}
        <div className="relative" ref={travellerPanelRef}>
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block">
            Travellers & Class
          </label>
          <button
            type="button"
            onClick={() => setTravellerPanelOpen(!travellerPanelOpen)}
            className="w-full px-3 py-2.5 text-left text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:border-primary focus:outline-none focus:border-primary transition-all flex items-center justify-between shadow-sm"
            aria-expanded={travellerPanelOpen}
          >
            <span>{travellerSummary}</span>
            <span className={`transition-transform text-gray-400 text-xs ${travellerPanelOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>

          {/* Traveller Panel Dropdown — opens upward so class section is always visible */}
          {travellerPanelOpen && (
            <div className="absolute z-50 mb-2 bg-white border border-gray-200 rounded-xl shadow-xl p-4 w-full min-w-[300px] bottom-full left-0">
              <div className="space-y-3">
                {[
                  { key: 'adults', label: 'Adults', sub: '12y+' },
                  { key: 'children', label: 'Children', sub: '2y–12y' },
                  { key: 'infants', label: 'Infants', sub: 'below 2y' },
                ].map(({ key, label, sub }) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">{label} <span className="text-xs font-normal text-gray-400">({sub})</span></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => handlePassengerCountChange(key, -1)}
                        className="w-8 h-8 rounded-full border border-gray-300 hover:border-primary hover:bg-blue-50 flex items-center justify-center text-gray-600 hover:text-primary font-bold transition-all">−</button>
                      <span className="w-5 text-center font-semibold text-gray-900">{formData[key]}</span>
                      <button type="button" onClick={() => handlePassengerCountChange(key, 1)}
                        className="w-8 h-8 rounded-full border border-gray-300 hover:border-primary hover:bg-blue-50 flex items-center justify-center text-gray-600 hover:text-primary font-bold transition-all">+</button>
                    </div>
                  </div>
                ))}

                <div className="pt-3 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Travel Class</div>
                  <div className="flex gap-1.5 flex-wrap">
                    {classOptions.map((option) => (
                      <button key={option} type="button" onClick={() => handleClassChange(option)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          formData.class === option
                            ? 'bg-primary text-white shadow-sm'
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}>
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                <button type="button" onClick={() => setTravellerPanelOpen(false)}
                  className="w-full py-2.5 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors shadow-sm text-sm">
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Section */}
      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-2 cursor-pointer group">
          <input type="checkbox" checked={directOnlySearch} onChange={(e) => onDirectOnlyChange(e.target.checked)}
            className="w-4 h-4 accent-primary cursor-pointer" />
          <span className="text-sm font-medium text-gray-600 group-hover:text-gray-900">Direct flights only</span>
        </label>

        <button type="submit" disabled={loading}
          className="w-full px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-xl font-bold text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg">
          {loading ? 'Searching...' : 'Search Flights'}
        </button>
      </div>
    </form>
  );
};

export default SearchFormTailwind;
